import { MapPin, Navigation, LoaderCircle, ArrowUpRight } from "lucide-react";
import { useExplore } from "@/lib/ExploreContext";
import { StaticSection } from "@/components/yts/HomeSections";
import { useLang } from "@/lib/i18n";

/**
 * Location-aware discovery. The visitor explicitly opts in before the browser
 * asks for their position; a refusal never blocks the rest of the guide.
 *
 * It reads as a note in the page rather than a panel over it: a peach plate for
 * the pin, the offer set in the guide's editorial serif, the hint under it, and
 * one dark pill carrying the action at the card's foot.
 */
export default function NearMeCard() {
  const { nearMe, locating, nearError, toggleNearMe } = useExplore();
  const { t, lang } = useLang();
  const isSpanish = lang === "es";

  const hint = locating
    ? t("nearMe.locating")
    : nearMe
      ? t("nearMe.active")
      : nearError
        ? t(nearError === "unavailable" ? "nearMe.unavailable" : "nearMe.error")
        : t("nearMe.hint");

  const actionLabel = locating
    ? (isSpanish ? "Buscando ubicación" : "Finding location")
    : nearMe
      ? (isSpanish ? "Desactivar cerca de mí" : "Turn off Near Me")
      : (isSpanish ? "Usar mi ubicación" : "Use my location");

  return (
    <StaticSection className="pt-3 sm:pt-4">
      <div className="relative overflow-hidden rounded-[22px] border border-[#e5e1d7] bg-white p-3 shadow-[0_16px_38px_-32px_rgba(20,18,15,0.6)] dark:border-white/[0.1] dark:bg-[#1c1a17] sm:rounded-[26px] sm:p-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-5">
          <span
            aria-hidden="true"
            className={`grid h-12 w-12 shrink-0 place-items-center rounded-[14px] bg-[#f9ebe5] text-[#171717] dark:bg-white/[0.09] dark:text-white sm:h-[52px] sm:w-[52px] ${
              locating ? "animate-pulse" : ""
            }`}
          >
            {nearMe ? (
              <Navigation className="h-[19px] w-[19px]" strokeWidth={1.9} />
            ) : (
              <MapPin className="h-[19px] w-[19px]" strokeWidth={1.9} />
            )}
          </span>

          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="text-[9px] font-black uppercase tracking-[0.24em] text-[#c62e2a]">
                {isSpanish ? "Cerca de ti" : "Near you"}
              </span>
              {nearMe && (
                <span className="inline-flex items-center gap-1 text-[8.5px] font-black uppercase tracking-[0.16em] text-[#2f7d52] dark:text-[#8ce0ac]">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#3fa86b] shadow-[0_0_6px_rgba(63,168,107,0.6)]" aria-hidden="true" />
                  {isSpanish ? "Activo" : "On"}
                </span>
              )}
            </div>

            <h2 className="mt-1.5 font-display text-[21px] font-semibold leading-[1.08] tracking-[-0.02em] text-[#111111] dark:text-white sm:text-[25px]">
              {isSpanish ? "Puerto Rico, a tu alrededor." : "Puerto Rico, all around you."}
            </h2>

            <p
              role={nearError ? "status" : undefined}
              aria-live="polite"
              className={`mt-1.5 max-w-[52ch] text-[12.5px] font-normal leading-[1.5] ${
                nearError ? "text-[#b3261e] dark:text-[#f6a79f]" : "text-[#6f6b64] dark:text-[#b7b1a7]"
              }`}
            >
              {hint}
            </p>
          </div>

          <button
            type="button"
            onClick={toggleNearMe}
            disabled={locating}
            aria-pressed={nearMe}
            className={`group inline-flex min-h-[46px] w-full shrink-0 items-center justify-center gap-2 rounded-full bg-[#171717] px-5 text-[10px] font-black uppercase tracking-[0.14em] text-[#f4a79b] transition-colors duration-200 hover:bg-black hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#171717]/40 focus-visible:ring-offset-2 disabled:cursor-wait disabled:opacity-75 dark:bg-[#f2efe9] dark:text-[#131211] dark:hover:bg-white sm:w-auto ${
              nearMe ? "dark:text-[#1f6b45]" : ""
            }`}
          >
            {locating ? (
              <LoaderCircle aria-hidden="true" className="h-4 w-4 animate-spin" />
            ) : nearMe ? (
              <Navigation aria-hidden="true" className="h-4 w-4" />
            ) : (
              <MapPin aria-hidden="true" className="h-4 w-4" strokeWidth={2.1} />
            )}
            <span>{actionLabel}</span>
            {!locating && (
              <ArrowUpRight
                aria-hidden="true"
                className="h-3.5 w-3.5 transition-transform duration-200 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
              />
            )}
          </button>
        </div>
      </div>
    </StaticSection>
  );
}